import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { useLocation, useNavigate } from 'react-router-dom'

import LoadingContext from '../../../../context/Loading/Loading'
import { useModals } from '../providers/ModalsProvider'

import authService from '../../../../utilities/services/auth.service'

import {
  getPaymentIntent,
  getCart,
  processFreeOrder,
  resolveFundraisingCampaign,
} from '../../../../utilities/api'

import { sort, isCartExpiredResponse } from '../../../../utilities/helpers'

import Card from 'react-bootstrap/Card'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'

import { BackButton } from '../../../BackButton'
import { PageLoadingContainer } from '../../../PageLoadingContainer'
import { PaymentMethod } from '../components/PaymentMethod'
import { GuestContact } from '../components/GuestContact'
import { Terms } from '../components/Terms'
import { BuyingThis } from '../../components/BuyingThis'
import { AdditionalInfo } from '../../components/AdditionalInfo'
import { Event } from '../../components/Event'
import { FundraisingDonation } from '../components/FundraisingDonation'

import '../../purchaseFlow.scss'
import './newCheckoutContainer.scss'
import useSeatmapStore from '../../../../stores/seatmapStore'
import { useTimeout } from '../providers/TimeoutProvider/TimeoutProvider'

import { injectMetaPixel } from '../../../../utilities/metaPixel'
import { trackBeginCheckout } from '../../../../utilities/tracking'
import useWaitingRoomHeartbeat from '../../../../hooks/useWaitingRoomHeartbeat'
import {
  hasValidWaitingRoomToken,
  isWaitingRoomRequired,
} from '../../../../utilities/waitingRoom'
import {
  trackCheckoutStarted,
  trackCheckoutCompleted,
} from '../../../../utilities/cartSessionTracking'
import { isZeroFeeCompFlexPackCart } from '@blocktickets/shared-utils'
import { readTrackingCode } from '../../../../utilities/trackingLink'
import {
  buildFundraisingPayload,
  buildPaymentIntentRequest,
  createInitialFundraisingSelection,
  getCartFundraisingContext,
  isFundraisingDonationSatisfied,
  resolveCheckoutFundraisingPricing,
} from '../fundraisingCheckout'

// Guest checkout is limited to plain event carts (GA + seated). Packages, flex
// packs and access passes still require an account because their post-purchase
// management lives behind /my-events. Mirrors isGuestEligibleCart on the server.
function isGuestEligibleCart(cart) {
  return Boolean(
    cart && !cart.package && !cart.flex_pack && !cart.access_pass_template
  )
}

function isComplimentaryWebsiteCart(cart) {
  if (!cart) return false
  if (cart.flex_pack && isZeroFeeCompFlexPackCart(cart.flex_pack)) return true
  if (cart.flex_pack || cart.package || cart.access_pass_template) return false
  const tickets = cart.tickets || []
  if (!tickets.length) return false
  return tickets.every((t) => {
    if (t.free === true) return true
    const o = t.offer
    if (!o || typeof o !== 'object') return false
    return o.freeOffer === true
  })
}

export default function NewCheckoutContainer() {
  const getTicketImage = useSeatmapStore((state) => state.getTicketImage)
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const { openModal } = useModals()
  const { startTimer, handleCartExpired } = useTimeout()

  const [clientSecret, setClientSecret] = useState('')
  const [status, setStatus] = useState('checkout')
  const [intentId, setIntentId] = useState('')
  const [pendingOrderId, setPendingOrderId] = useState('')
  const [event, setEvent] = useState()
  const [order, setOrder] = useState()
  const [cart, setCart] = useState(null)
  const [stripe, setStripe] = useState()
  const [stripeAccountContext, setStripeAccountContext] = useState(undefined)
  const [fundraisingCampaign, setFundraisingCampaign] = useState(null)
  const [fundraisingSelection, setFundraisingSelection] = useState(
    createInitialFundraisingSelection()
  )
  const [isRefreshingIntent, setIsRefreshingIntent] = useState(false)
  // Guests supply contact details here instead of registering first; the
  // payment intent is deferred until they do.
  const [guestBuyer, setGuestBuyer] = useState(null)
  const [guestError, setGuestError] = useState('')

  const cartRef = useRef(null)
  const eventRef = useRef(null)
  const paymentContextRef = useRef(null)
  const guestBuyerRef = useRef(null)

  const isLoggedIn = authService.isLoggedIn()
  const checkoutUnlocked = isLoggedIn || Boolean(guestBuyer)

  const navigate = useNavigate()
  const location = useLocation()

  useWaitingRoomHeartbeat(
    event?.uuid,
    isWaitingRoomRequired(event) && hasValidWaitingRoomToken(event?.uuid)
  )

  const buildStripe = async (connectedAccountID) => {
    const stripeObj = connectedAccountID
      ? await loadStripe(process.env.REACT_APP_STRIPE_KEY, {
          stripeAccount: connectedAccountID,
        })
      : await loadStripe(process.env.REACT_APP_STRIPE_KEY)
    setStripe(stripeObj)
  }

  const loadPaymentIntent = useCallback(
    async (cartData, eventData, fundraisingPayload) => {
      const request = buildPaymentIntentRequest(
        cartData,
        eventData,
        fundraisingPayload,
        guestBuyerRef.current
      )
      const response = await getPaymentIntent(request)
      const isDestinationCharge = Boolean(
        response?.data?.transfer_data?.destination
      )
      const connectedAccountId = paymentContextRef.current?.connectedAccountId
      setStripeAccountContext(isDestinationCharge ? null : connectedAccountId)
      setClientSecret(response.data.client_secret)
      setIntentId(response.data.id)
      setPendingOrderId(response?.data?.metadata?.order_id || '')
      return response
    },
    []
  )

  const handleFundraisingChange = useCallback(
    async (nextSelection) => {
      setFundraisingSelection(nextSelection)
      const currentCart = cartRef.current
      const currentEvent = eventRef.current
      if (!currentCart) return
      // Guests have no payment intent to refresh until they submit contact info.
      if (!isLoggedIn && !guestBuyerRef.current) return
      // Flex pack / access pass / package checkout can proceed without a single event.
      if (
        !currentCart.flex_pack &&
        !currentCart.access_pass_template &&
        !currentCart.package &&
        !currentEvent
      )
        return

      const fundraisingPayload = buildFundraisingPayload(
        fundraisingCampaign,
        nextSelection
      )

      setIsRefreshingIntent(true)
      try {
        await loadPaymentIntent(
          currentCart,
          currentEvent ?? null,
          fundraisingPayload
        )
      } catch (err) {
        console.error('Failed to refresh payment intent for donation:', err)
        if (isCartExpiredResponse(err)) {
          handleCartExpired()
        }
      } finally {
        setIsRefreshingIntent(false)
      }
    },
    [fundraisingCampaign, handleCartExpired, isLoggedIn, loadPaymentIntent]
  )

  const handleGuestConfirm = useCallback(
    async (buyer) => {
      guestBuyerRef.current = buyer
      setGuestError('')
      showLoading()
      try {
        await loadPaymentIntent(
          cartRef.current,
          eventRef.current ?? null,
          buildFundraisingPayload(fundraisingCampaign, fundraisingSelection)
        )
        setGuestBuyer(buyer)
        // The success screen has no session to read the buyer from, and the
        // order lookup there is public so it deliberately omits contact info.
        sessionStorage.setItem(
          'guestCheckout',
          JSON.stringify({ email: buyer.email })
        )
      } catch (err) {
        console.error('Failed to start guest checkout:', err)
        guestBuyerRef.current = null
        if (isCartExpiredResponse(err)) {
          handleCartExpired()
          return
        }
        setGuestError(
          err?.response?.data?.error?.message ||
            'We could not start your checkout. Please check your details and try again.'
        )
      } finally {
        hideLoading()
      }
    },
    [
      fundraisingCampaign,
      fundraisingSelection,
      handleCartExpired,
      hideLoading,
      loadPaymentIntent,
      showLoading,
    ]
  )

  useEffect(() => {
    showLoading()

    let cart = sessionStorage.getItem('cart')
    if (cart) cart = JSON.parse(cart)

    let cartId
    if (cart?.cartId) {
      cartId = cart.cartId
    }

    getCart(cartId)
      .then(async (res) => {
        const cartData = res.data
        setCart(cartData)
        cartRef.current = cartData

        if (cartData?.remainingTime !== undefined) {
          const eventUUID = cartData?.event?.uuid
          const remainingTimeWithBuffer = cartData?.remainingTime - 30000
          startTimer(remainingTimeWithBuffer, eventUUID)
        }

        if (isComplimentaryWebsiteCart(cartData)) {
          // Complimentary orders are not guest-eligible; they still need an
          // account so the free inventory can be tied to a real fan.
          if (!isLoggedIn) {
            hideLoading()
            navigate('/login', { state: { from: location } })
            return
          }
          processFreeOrder({
            cartId: cartData.id,
            trackingCode: readTrackingCode(
              cartData?.eventUUID || cartData?.event?.uuid
            ),
          })
            .then((processRes) => {
              sessionStorage.setItem(
                'order',
                JSON.stringify({ id: processRes.data?.id })
              )
              navigate(
                `/checkout/checkout-success?intentId=${encodeURIComponent(processRes.data?.paymentIntentId)}`,
                { replace: true }
              )
              hideLoading()
            })
            .catch((err) => {
              console.error('Complimentary checkout failed:', err)
              hideLoading()
              if (isCartExpiredResponse(err)) {
                handleCartExpired()
              }
            })
          return
        }

        // Packages, flex packs and access passes are not guest-eligible; the
        // server 401s these for logged-out buyers, so bounce them to login here
        // instead of showing a guest form that can never complete.
        if (!isLoggedIn && !isGuestEligibleCart(cartData)) {
          hideLoading()
          navigate('/login', { state: { from: location } })
          return
        }

        const eventData = cartData.package
          ? sort(cartData.package.events || [])?.[0]
          : cartData.access_pass_template
            ? sort(cartData.access_pass_template.events || [])?.[0]
            : cartData.event
        const organization =
          cartData?.event?.organization ||
          cartData?.package?.organization ||
          cartData?.flex_pack?.organization ||
          cartData?.access_pass_template?.organization ||
          eventData?.organization
        const isProdEnv = process.env.REACT_APP_CLIENT_ENV === 'production'
        const connectedAccountId = cartData?.flex_pack
          ? isProdEnv
            ? cartData?.flex_pack?.organization?.connected_account_id
            : cartData?.flex_pack?.organization?.test_connected_account_id
          : cartData?.access_pass_template
            ? isProdEnv
              ? cartData?.access_pass_template?.organization
                  ?.connected_account_id
              : cartData?.access_pass_template?.organization
                  ?.test_connected_account_id
            : cartData?.package
              ? isProdEnv
                ? organization?.connected_account_id
                : organization?.test_connected_account_id
              : isProdEnv
                ? eventData?.organization?.connected_account_id
                : eventData?.organization?.test_connected_account_id
        const pixelId = cartData?.flex_pack
          ? cartData?.flex_pack?.organization?.meta_pixel_id
          : cartData?.access_pass_template
            ? cartData?.access_pass_template?.organization?.meta_pixel_id
            : cartData?.package
              ? organization?.meta_pixel_id
              : eventData?.organization?.meta_pixel_id

        const nextPaymentContext = {
          connectedAccountId,
          organization,
          isProdEnv,
        }
        paymentContextRef.current = nextPaymentContext

        if (pixelId) injectMetaPixel(pixelId)
        trackBeginCheckout({
          organization,
          cart: cartData,
        })
        trackCheckoutStarted(cartData.id)
        setEvent(eventData)
        eventRef.current = eventData

        let resolvedCampaign = null
        try {
          const resolveContext = getCartFundraisingContext(cartData)
          if (resolveContext.organizationUUID) {
            const resolveRes = await resolveFundraisingCampaign(resolveContext)
            resolvedCampaign = resolveRes.data?.campaign || null
          }
        } catch (resolveError) {
          console.error('Failed to resolve fundraising campaign:', resolveError)
        }

        setFundraisingCampaign(resolvedCampaign)
        const initialSelection =
          createInitialFundraisingSelection(resolvedCampaign)
        setFundraisingSelection(initialSelection)

        // Guests must give us an email before we can create a payment intent,
        // so the intent is created when they submit the contact form instead.
        if (isLoggedIn) {
          await loadPaymentIntent(
            cartData,
            eventData ?? null,
            buildFundraisingPayload(resolvedCampaign, initialSelection)
          )
        }
        hideLoading()
      })
      .catch((err) => {
        hideLoading()
        if (isCartExpiredResponse(err)) {
          handleCartExpired()
          return
        }
        sessionStorage.removeItem('cart')
        navigate(-1)
      })
  }, [])

  useEffect(() => {
    if (window.Intercom) {
      window.Intercom('update', {
        hide_default_launcher: true,
      })
    }
  }, [])

  useEffect(() => {
    if (stripeAccountContext !== undefined) buildStripe(stripeAccountContext)
  }, [stripeAccountContext])

  const fundraisingPayload = buildFundraisingPayload(
    fundraisingCampaign,
    fundraisingSelection
  )
  const checkoutCart = fundraisingPayload
    ? { ...cart, fundraising: fundraisingPayload }
    : cart
  const donationRequirementMet = isFundraisingDonationSatisfied(
    fundraisingCampaign,
    fundraisingSelection
  )
  const donationPricing = resolveCheckoutFundraisingPricing(
    fundraisingCampaign,
    fundraisingSelection
  )

  const addOns = []

  const appearance = {
    theme: 'flat',
    variables: {
      fontFamily: ' "Poppins", sans-serif',
      fontLineHeight: '1.4',
      fontSizeBase: '14px',
      fontSizeSm: '12px',
      fontWeightNormal: '500',
      borderRadius: '12px',
      colorPrimary: '#5ab6f8',
      colorBackground: '#FFFFFF',
      colorText: '#23262F',
      colorWarning: '#EF466F',
      colorDanger: '#EF466F',
      colorTextSecondary: '#777e90',
      spacingUnit: '12px',
      spacingGridRow: '24px',
    },
    rules: {
      '.Input': {
        boxShadow: '0px 0px 0px 2px #E6E8EC',
        padding: '12.250px 14.875px',
        lineHeight: 'var(--fontLineHeight)',
      },

      '.Input:focus': {
        outline: '0',
        boxShadow: '0px 0px 0px 2px var(--colorPrimary)',
      },
      '.Input:disabled, .Input--invalid:disabled': {
        color: 'lightgray',
      },
      '.Tab': {
        boxShadow: '0px 0px 0px 2px #E6E8EC',
        padding: '12.250px 14.875px',
        border: 'none',
      },
      '.Tab--selected, .Tab--selected:focus, .Tab--selected:hover': {
        border: 'none',
        boxShadow: '0px 0px 0px 2px var(--colorPrimary)',
        backgroundColor: 'var(--colorPrimary)',
      },
      '.Label': {
        fontWeight: '700',
        textTransform: 'uppercase',
        color: 'var(--colorTextSecondary)',
        marginBottom: 'var(--spacingUnit)',
        lineHeight: 'var(--fontLineHeight)',
      },

      '.Input--invalid': {
        boxShadow:
          '0 1px 1px 0 rgba(0, 0, 0, 0.07), 0 0 0 2px var(--colorDanger)',
      },

      '.Error': {
        marginTop: 'var(--spacingUnit)',
      },
    },
  }

  const options = {
    clientSecret,
    appearance,
    loader: 'always',
    layout: {
      type: 'accordion',
      defaultCollapsed: false,
      radios: true,
      spacedAccordionItems: false,
    },
  }

  const handleGoBack = () => {
    openModal('Are you sure?', event)
  }

  return (
    <>
      {isLoading ? (
        <PageLoadingContainer
          text="Securing your tickets"
          subtext="You will be redirected to the payment page."
          showLoader={false}
          icon="ticket"
        />
      ) : (
        <div className="new-checkout-page purchaseflow-gradient purchaseflow-gradient--tablet-desktop-only">
          <div className="desktop-only">
            <BackButton handleGoBack={handleGoBack} />
          </div>
          <div className="pfselect-wrapper">
            <div className="tablet-desktop-only">
              <Event
                flexPack={cart?.flex_pack}
                accessPassTemplate={cart?.access_pass_template}
                eventPackage={cart?.package}
                event={event}
                openModal={openModal}
              />
            </div>
            <Stack
              direction="horizontal"
              className="bg-white pt-2 px-3 mobile-only"
            >
              <Button
                variant="default"
                size="sm"
                className="btn--icon flex-shrink-0 mobile-only border-0 w-auto"
                onClick={handleGoBack}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  role="img"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M15.7071 19.2071C16.0976 18.8166 16.0976 18.1834 15.7071 17.7929L9.91421 12L15.7071 6.20711C16.0976 5.81658 16.0976 5.18342 15.7071 4.79289C15.3166 4.40237 14.6834 4.40237 14.2929 4.79289L8.5 10.5858C7.71895 11.3668 7.71895 12.6332 8.5 13.4142L14.2929 19.2071C14.6834 19.5976 15.3166 19.5976 15.7071 19.2071Z"
                    fill="#777E91"
                  />
                </svg>
              </Button>
              <span className="flex-grow-1 text-center caption-bold">
                Checkout
              </span>
            </Stack>
            <Card className="card-xl card-xl--dark great-wrapper">
              <Card.Body className="wrapper-body pt-0 pt-md-4">
                <div className="card-body-heading g-texts tablet-desktop-only">
                  <Card.Title as="h5">You're so close!</Card.Title>
                  <Card.Subtitle as="h6">
                    Select your payment method and complete your purchase.
                  </Card.Subtitle>
                </div>
                {!cart?.flex_pack && !cart?.access_pass_template && (
                  <BuyingThis
                    cart={cart}
                    event={event}
                    getTicketImage={getTicketImage}
                  />
                )}
                {!checkoutUnlocked && (
                  <GuestContact
                    onConfirm={handleGuestConfirm}
                    submitError={guestError}
                    organizationName={
                      cart?.event?.organization?.name ||
                      cart?.package?.organization?.name ||
                      cart?.flex_pack?.organization?.name ||
                      cart?.access_pass_template?.organization?.name ||
                      event?.organization?.name
                    }
                    optInMessage={
                      cart?.event?.organization?.opt_in_message ||
                      cart?.package?.organization?.opt_in_message ||
                      cart?.flex_pack?.organization?.opt_in_message ||
                      cart?.access_pass_template?.organization
                        ?.opt_in_message ||
                      event?.organization?.opt_in_message
                    }
                  />
                )}
                {checkoutUnlocked && (
                  <>
                    {fundraisingCampaign && (
                      <FundraisingDonation
                        campaign={fundraisingCampaign}
                        selection={fundraisingSelection}
                        onChange={handleFundraisingChange}
                        disabled={isRefreshingIntent}
                      />
                    )}
                    <PaymentMethod
                      key={clientSecret || 'checkout-payment'}
                      clientSecret={clientSecret}
                      stripePromise={stripe}
                      options={options}
                      status={status}
                      event={event}
                      addOns={addOns}
                      setStatus={setStatus}
                      intentId={intentId}
                      pendingOrderId={pendingOrderId}
                      order={order}
                      cart={checkoutCart}
                      openModal={openModal}
                      donationPricing={donationPricing}
                      donationRequirementMet={donationRequirementMet}
                      isRefreshingIntent={isRefreshingIntent}
                    />
                  </>
                )}
                <Terms />
                <AdditionalInfo
                  bg="light"
                  MTDescription={
                    isLoggedIn
                      ? 'Securely stored in your account.'
                      : `Emailed to ${guestBuyer?.email || 'you'} right after payment.`
                  }
                  BPDescription={'Safe from bots and scalpers.'}
                />
              </Card.Body>
            </Card>
          </div>
        </div>
      )}
    </>
  )
}
